"use client";

import type { Facet } from "../../hooks/useLifestyleFilters";

interface FacetListProps {
  legend: string;
  facets: Facet[];
  onToggle: (value: string) => void;
}

/** Figma Component 26 — unchecked (16097:89438) and checked (16097:89450). */
function FilterCheckbox({ checked }: { checked: boolean }) {
  if (checked) {
    return (
      <svg viewBox="0 0 20 20" className="shrink-0 size-5" aria-hidden="true" fill="none">
        <path
          d="M15.2775 1.66667C16.9108 1.66667 18.2442 2.9475 18.3292 4.56L18.3333 4.7225V15.2775C18.3333 16.9108 17.0525 18.2442 15.44 18.3292L15.2775 18.3333H4.7225C3.94015 18.3334 3.18758 18.0333 2.61984 17.4951C2.0521 16.9568 1.71244 16.2212 1.67083 15.44L1.66667 15.2775V4.7225C1.66667 3.08917 2.9475 1.75583 4.56 1.67083L4.7225 1.66667H15.2775ZM13.0892 7.74417C12.9329 7.58794 12.721 7.50018 12.5 7.50018C12.279 7.50018 12.0671 7.58794 11.9108 7.74417L9.16667 10.4875L8.08917 9.41083L8.01083 9.34167C7.84334 9.21216 7.63283 9.15126 7.42206 9.17133C7.21129 9.19141 7.01607 9.29096 6.87604 9.44977C6.73601 9.60857 6.66168 9.81472 6.66814 10.0263C6.67461 10.238 6.76138 10.4392 6.91083 10.5892L8.5775 12.2558L8.65583 12.325C8.81617 12.4494 9.01638 12.511 9.2189 12.4982C9.42142 12.4855 9.61234 12.3993 9.75583 12.2558L13.0892 8.9225L13.1583 8.84417C13.2827 8.68383 13.3443 8.48362 13.3316 8.2811C13.3188 8.07858 13.2326 7.88766 13.0892 7.74417Z"
          fill="#009922"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 20 20" className="shrink-0 size-5 text-gray-5" aria-hidden="true" fill="none">
      <path
        d="M2.5 4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H15.8333C16.2754 2.5 16.6993 2.67559 17.0118 2.98816C17.3244 3.30072 17.5 3.72464 17.5 4.16667V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667Z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A checkbox list with result counts, used for makes, styles and the banded
 * measurements alike — all three are the same "tick some options" screen.
 *
 * The legend is screen-reader only: this fills its own drawer screen, which
 * already carries the name in the header.
 */
export default function FacetList({ legend, facets, onToggle }: FacetListProps) {
  if (facets.length === 0) return null;

  return (
    <fieldset className="flex flex-col w-full">
      <legend className="sr-only">{legend}</legend>

      {facets.map(facet => (
        <label
          key={facet.value}
          className={`flex items-center justify-between w-full py-[9px] gap-3 text-base font-roboto leading-5.5 ${
            facet.disabled ? "text-gray-8 cursor-not-allowed" : "text-dark-1 cursor-pointer"
          }`}>
          <span className="flex items-center min-w-0 gap-2">
            <span className={`relative shrink-0 size-5 ${facet.disabled ? "opacity-38" : ""}`}>
              <input
                type="checkbox"
                checked={facet.selected}
                disabled={facet.disabled}
                onChange={() => onToggle(facet.value)}
                className="absolute inset-0 z-[1] size-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
              <FilterCheckbox checked={facet.selected} />
            </span>
            <span className="truncate">{facet.label ?? facet.value}</span>
          </span>
          <span className="flex items-center justify-center shrink-0 h-5 min-w-5 px-2 text-[11px] font-semibold leading-3.5 text-gray-1 bg-[#e7e7e9] rounded-full">
            {facet.count}
          </span>
        </label>
      ))}
    </fieldset>
  );
}
